import React, { useState } from 'react';
import type { MediaAsset } from '../../../types/workspace';
import SecondaryActionButton from '../buttons/SecondaryActionButton';
import SmartImagePreview from '../../../components/shared/SmartImagePreview';

interface ImageUploadFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  assets: MediaAsset[];
  onUpload: (file: File) => Promise<MediaAsset>;
  onAssetSelected?: (asset: MediaAsset) => void;
  helpText?: string;
}

const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
  label,
  value,
  onChange,
  assets,
  onUpload,
  onAssetSelected,
  helpText,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadFeedback, setUploadFeedback] = useState<string | null>(null);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setIsUploading(true);
    setUploadError(null);
    setUploadFeedback(null);
    try {
      const asset = await onUpload(file);
      onChange(asset.url);
      onAssetSelected?.(asset);
      setUploadFeedback(`Uploaded ${asset.altText || file.name}`);
    } catch (caughtError) {
      setUploadError(caughtError instanceof Error ? caughtError.message : 'Could not upload image');
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  return (
    <div className="space-y-4 rounded-[28px] border border-white/10 bg-white/5 p-4">
      <div>
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.3em] text-amber-100/55">{label}</div>
        <input
          type="url"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="https://..."
          className="w-full rounded-2xl border border-white/10 bg-[#120807] px-4 py-3 text-white placeholder:text-white/35 focus:border-amber-400/35 focus:outline-none"
        />
        {helpText && <div className="mt-2 text-sm text-white/45">{helpText}</div>}
      </div>

      {value && (
        <SmartImagePreview src={value} alt={label} className="h-56" />
      )}

      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex cursor-pointer">
          <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
          <SecondaryActionButton disabled={isUploading}>
            {isUploading ? 'Uploading...' : 'Upload from Browser'}
          </SecondaryActionButton>
        </label>
        <div className="text-sm text-white/45">
          Transparent PNG and WebP images keep their full shape automatically.
        </div>
      </div>

      {uploadError && <div className="text-sm text-red-200">{uploadError}</div>}
      {uploadFeedback && <div className="text-sm text-emerald-100">{uploadFeedback}</div>}

      {assets.length > 0 && (
        <div>
          <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-amber-100/55">Reuse Uploaded Media</div>
          <div className="grid gap-3 sm:grid-cols-2">
            {assets.slice(0, 6).map((asset) => (
              <button
                key={asset.id}
                type="button"
                onClick={() => {
                  onChange(asset.url);
                  onAssetSelected?.(asset);
                }}
                className={`overflow-hidden rounded-2xl border text-left transition ${
                  value === asset.url ? 'border-amber-300/40' : 'border-white/10 hover:border-white/20'
                }`}
              >
                <SmartImagePreview src={asset.url} alt={asset.altText || label} className="h-24 rounded-none border-0" />
                <div className="p-3 text-xs text-white/65">{asset.altText || asset.kind}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploadField;
